import React from "react";
import styled from "styled-components";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { times } from "lodash";
import * as Sentry from "@sentry/react";
import TextLink from "../library/TextLink";
import { TextInputField } from "../library/Form";
import SubmitButton from "../library/Form/SubmitButton";
import RecruiterUser from "../../apiResources/RecruiterUser";
import EmployerUser from "../../apiResources/EmployerUser";
import SettingLabel from "./SettingLabel";
import SettingHelpText from "./SettingHelperText";
import { formatCurrentPasswordError } from "./utils";

const PasswordSettingOption = <User extends RecruiterUser | EmployerUser>(
  props: Props<User>
) => {
  const {
    user,
    isEditing = false,
    disabled = false,
    onEdit,
    onDone,
    onUserChange,
  } = props;

  const save = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => {
    try {
      user.assignAttributes(values);
      const success = await user.save();
      if (success) {
        onUserChange?.(user);
        onDone();
      } else {
        formik.setErrors({
          currentPassword: formatCurrentPasswordError(
            user.errors.currentPassword
          ),
          password: user.errors.password.fullMessage,
          passwordConfirmation: user.errors.passwordConfirmation.fullMessage,
        });
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  const edit = () => {
    if (!disabled) {
      onEdit();
    }
  };

  if (!isEditing) {
    return (
      <section>
        <Header>
          <SettingLabel>Password</SettingLabel>
          <TextLink disabled={disabled} onClick={edit}>
            Edit
          </TextLink>
        </Header>
        <div>
          {times(10).map((key) => (
            <React.Fragment key={key}>&bull;</React.Fragment>
          ))}
        </div>
      </section>
    );
  }

  const initialValues: FormValues = {
    currentPassword: "",
    password: "",
    passwordConfirmation: "",
  };

  const validation = Yup.object({
    currentPassword: Yup.string().required("Please enter your password"),
    password: Yup.string()
      .required("Please enter a new password")
      .matches(
        /^(?=.*[a-z])/,
        "Please make sure your password contains a lowercase letter"
      )
      .matches(
        /^(?=.*[A-Z])/,
        "Please make sure your password contains an uppercase letter"
      )
      .matches(
        /^(?=.*[0-9])/,
        "Please make sure your password contains a number"
      )
      .min(6, "Please make sure your password is at least 6 characters")
      .max(
        128,
        "Please make sure your password is not longer than 128 characters"
      ),
    passwordConfirmation: Yup.string()
      .required("Please confirm your new password")
      .oneOf(
        [Yup.ref("password"), null],
        "Please make sure your new password matches"
      ),
  });

  return (
    <section>
      <Formik
        initialValues={initialValues}
        onSubmit={save}
        validationSchema={validation}
      >
        <Form autoComplete="off">
          <Header>
            <SettingLabel>Password</SettingLabel>
            <TextLink onClick={onDone}>Cancel</TextLink>
          </Header>
          <SettingHelpText>
            Your password must include a lowercase letter, an uppercase letter,
            a number and be 6 or more characters long
          </SettingHelpText>
          <TextInputField
            label="Current password"
            type="password"
            name="currentPassword"
          />
          <TextInputField
            label="New password"
            type="password"
            name="password"
          />
          <TextInputField
            label="Confirm new password"
            type="password"
            name="passwordConfirmation"
          />
          <SubmitButton />
        </Form>
      </Formik>
    </section>
  );
};

type Props<User> = {
  user: User;
  isEditing?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onEdit: () => void;
  onDone: () => void;
  onUserChange?: (user: User) => void;
};

type FormValues = {
  currentPassword: string;
  password: string;
  passwordConfirmation: string;
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export default PasswordSettingOption;
